import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    ShieldOff, ShieldCheck, KeyRound, UserCog, Eye, Save, AlertTriangle,
    UserPlus, Trash2, Users as UsersIcon, EyeIcon, Mail, Copy, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../store/auth';
import CreateUserModal from '../../components/CreateUserModal.jsx';
import { useIsViewOnly } from '../../utils/viewOnly';

const SALES_DESIGNATIONS = [
    { value: '', label: '— not set —' },
    { value: 'bda', label: 'BDA' },
    { value: 'bde', label: 'BDE' },
    { value: 'bdm', label: 'BDM' },
    { value: 'bhead', label: 'BHead' },
    { value: 'operations', label: 'Operations' },
    { value: 'performance_marketing', label: 'Performance Marketing' },
    { value: 'other', label: 'Other' },
];

const FALLBACK_OFFICES = ['Noida', 'Zirakpur', 'Chandigarh', 'Mumbai', 'Hyderabad'];

const STATUS_CHIP = {
    pending: 'bg-amber-100 text-amber-700',
    active: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-rose-100 text-rose-700',
    suspended: 'bg-ink-100 text-ink-700',
};

function IconBtn({ title, onClick, children, danger, primary }) {
    const cls = danger
        ? 'text-rose-600 hover:bg-rose-50'
        : primary
            ? 'text-brand-teal-700 hover:bg-brand-teal-50'
            : 'text-ink-600 hover:bg-ink-100';
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            onClick={onClick}
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${cls}`}
        >
            {children}
        </button>
    );
}

export default function Users() {
    const { user: me } = useAuth();
    const isViewOnly = useIsViewOnly();
    const [items, setItems] = useState([]);
    const [roles, setRoles] = useState([]);
    const [offices, setOffices] = useState(FALLBACK_OFFICES);
    const [stats, setStats] = useState({ total: 0, active: 0, pending: 0 });
    const [filters, setFilters] = useState({ q: '', status: '', role: '', officeBranch: '' });
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    /* K2 — view-only invite modal state. Kept local rather than threading
       through CreateUserModal because the invite form is intentionally
       minimal: name + email only. */
    const [inviteOpen, setInviteOpen] = useState(false);

    const officeOptions = useMemo(() => {
        const fromDir = offices.map(o => (typeof o === 'string' ? o : o.label));
        const fromUsers = items.map(u => u.officeBranch).filter(Boolean);
        return [...new Set([...fromDir, ...fromUsers])].sort((a, b) => a.localeCompare(b));
    }, [offices, items]);

    const load = async () => {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        const { data } = await api.get('/users', { params });
        setItems(data.items);
        setEdits({});
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(filters)]);

    useEffect(() => {
        api.get('/roles').then(r => setRoles(r.data.items)).catch(() => {});
        api.get('/users/stats').then(r => setStats(r.data)).catch(() => {});
        api.get('/auth/directory')
            .then(r => {
                const list = (r.data?.offices || []).map(o => o.label).filter(Boolean);
                if (list.length) setOffices(list);
            })
            .catch(() => {});
    }, []);

    const fieldValue = (u, field) => {
        const e = edits[u._id];
        return e && field in e ? e[field] : (u[field] || '');
    };
    const setField = (uid, field, value) => setEdits(e => ({
        ...e,
        [uid]: { ...(e[uid] || {}), [field]: value },
    }));
    const dirtyCount = useMemo(
        () => Object.values(edits).filter(e => Object.keys(e).length > 0).length,
        [edits],
    );

    const hasActiveFilters = !!(filters.q || filters.status || filters.role || filters.officeBranch);

    const saveAll = async () => {
        if (dirtyCount === 0) return;
        setSaving(true);
        try {
            await Promise.all(Object.entries(edits).map(([uid, patch]) =>
                api.patch(`/users/${uid}`, patch).catch(() => null),
            ));
            toast.success(`Saved ${dirtyCount} user${dirtyCount === 1 ? '' : 's'}`);
            await load();
        } finally { setSaving(false); }
    };
    const discardAll = () => setEdits({});

    const roleBySlug = (slug) => roles.find(r => r.slug === slug);

    const suspend = async (u) => {
        const reason = prompt(`Suspend ${u.name}? Reason:`);
        if (reason === null) return;
        await api.post(`/users/${u._id}/suspend`, { reason });
        toast.success(`${u.name} suspended`);
        load();
    };
    const reactivate = async (u) => {
        await api.post(`/users/${u._id}/reactivate`);
        toast.success(`${u.name} reactivated`);
        load();
    };
    const reset = async (u) => {
        const newPassword = prompt(`Reset password for ${u.name}? Enter new password (min 8 chars):`);
        if (!newPassword) return;
        await api.post(`/users/${u._id}/reset-password`, { newPassword });
        toast.success('Password reset');
    };
    const changeRole = async (u) => {
        const opts = roles.map(r => `${r.slug}  —  ${r.name}`).join('\n');
        const slug = prompt(`Change role for ${u.name}.\n\nAvailable:\n${opts}\n\nEnter slug:`, u.role);
        if (!slug || slug === u.role) return;
        const exists = roles.find(r => r.slug === slug.trim());
        if (!exists) return toast.error(`No role with slug "${slug}"`);
        await api.patch(`/users/${u._id}`, { role: slug.trim() });
        toast.success(`${u.name} → ${exists.name}`);
        load();
    };
    const removeUser = async (u) => {
        if (String(u._id) === String(me?._id)) {
            return toast.error('You cannot delete your own account.');
        }
        if (!confirm(`Permanently delete ${u.name} (${u.email})?\n\nThis cannot be undone. Users linked to clients or incentives must be suspended instead.`)) return;
        try {
            await api.delete(`/users/${u._id}`);
            toast.success(`${u.name} deleted`);
            load();
        } catch { /* toasted */ }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    <h1 className="text-2xl font-extrabold">Users</h1>
                    <p className="text-sm text-ink-500 mt-1 max-w-2xl">
                        Manage accounts, designations, and office branches for the sales team.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-ink-100 px-3 py-1 text-xs font-semibold text-ink-700">
                            <UsersIcon className="h-3.5 w-3.5" />
                            {stats.total} total
                        </span>
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                            {stats.active} active
                        </span>
                        <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                            {stats.pending} pending
                        </span>
                        {hasActiveFilters && (
                            <span className="inline-flex items-center rounded-full bg-brand-teal-50 px-3 py-1 text-xs font-semibold text-brand-teal-800">
                                {items.length} matching filter{items.length === 1 ? '' : 's'}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                    {!isViewOnly && (
                        <>
                            <button type="button" className="ag-btn-ghost" onClick={() => setInviteOpen(true)}
                                title="Invite a read-only auditor — sees everything, can't write">
                                <EyeIcon className="h-4 w-4" /> Invite view-only
                            </button>
                            <button type="button" className="ag-btn-primary" onClick={() => setCreateOpen(true)}>
                                <UserPlus className="h-4 w-4" /> Create user
                            </button>
                            {dirtyCount > 0 && (
                                <>
                                    <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                                        <AlertTriangle className="h-3.5 w-3.5" /> {dirtyCount} unsaved
                                    </span>
                                    <button type="button" className="ag-btn-ghost text-xs" onClick={discardAll}>Discard</button>
                                    <button type="button" className="ag-btn-primary" disabled={saving} onClick={saveAll}>
                                        <Save className="h-4 w-4" /> {saving ? 'Saving…' : `Save ${dirtyCount}`}
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {createOpen && (
                <CreateUserModal
                    roles={roles}
                    onClose={(saved) => { setCreateOpen(false); if (saved) load(); }}
                />
            )}

            {inviteOpen && (
                <InviteViewOnlyModal
                    onClose={(invited) => { setInviteOpen(false); if (invited) load(); }}
                />
            )}

            <div className="ag-card p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    <div className="sm:col-span-2 xl:col-span-1">
                        <label className="ag-label">Search</label>
                        <input
                            className="ag-input"
                            placeholder="Name, email, territory…"
                            value={filters.q}
                            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="ag-label">Status</label>
                        <select className="ag-input" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                            <option value="">All</option>
                            <option value="pending">Pending</option>
                            <option value="active">Active</option>
                            <option value="rejected">Rejected</option>
                            <option value="suspended">Suspended</option>
                        </select>
                    </div>
                    <div>
                        <label className="ag-label">Role</label>
                        <select className="ag-input" value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}>
                            <option value="">All</option>
                            {roles.map(r => <option key={r._id} value={r.slug}>{r.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="ag-label">Office / location</label>
                        <select className="ag-input" value={filters.officeBranch} onChange={e => setFilters(f => ({ ...f, officeBranch: e.target.value }))}>
                            <option value="">All locations</option>
                            {officeOptions.map(b => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="ag-card overflow-hidden">
                <div className="px-4 py-2.5 border-b border-ink-100 bg-ink-50/50 text-xs text-ink-600 flex items-center justify-between">
                    <span>
                        Showing <b className="text-ink-900">{items.length}</b> user{items.length === 1 ? '' : 's'}
                        {hasActiveFilters ? ` (filtered from ${stats.total})` : ''}
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="ag-table w-full min-w-[900px]">
                        <thead>
                            <tr>
                                <th className="w-10 text-center">#</th>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Designation</th>
                                <th>Office</th>
                                <th>Joined</th>
                                <th className="w-[1%] whitespace-nowrap text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((u, idx) => {
                                const r = roleBySlug(u.role);
                                const rowDirty = edits[u._id] && Object.keys(edits[u._id]).length > 0;
                                return (
                                    <tr key={u._id} className={rowDirty ? 'bg-amber-50/50' : ''}>
                                        <td className="text-center text-xs text-ink-400 font-medium tabular-nums">
                                            {idx + 1}
                                        </td>
                                        <td className="min-w-[160px]">
                                            <Link to={`/admin/users/${u._id}`} className="font-semibold text-sm hover:text-brand-teal-700 hover:underline">
                                                {u.name}
                                            </Link>
                                            <div className="text-[11px] text-ink-500 truncate max-w-[200px]">{u.email}</div>
                                        </td>
                                        <td className="whitespace-nowrap">
                                            <span className="ag-chip text-xs" style={{ background: (r?.color || '#00B09B') + '22', color: r?.color || '#00B09B' }}>
                                                {r?.name || u.role}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap">
                                            <span className={`ag-chip text-xs capitalize ${STATUS_CHIP[u.status]}`}>{u.status}</span>
                                        </td>
                                        <td>
                                            <select
                                                className="ag-input text-xs py-1.5 w-full min-w-[110px] max-w-[140px]"
                                                value={fieldValue(u, 'salesDesignation')}
                                                onChange={e => setField(u._id, 'salesDesignation', e.target.value)}
                                                disabled={isViewOnly}
                                            >
                                                {SALES_DESIGNATIONS.map(d => (
                                                    <option key={d.value} value={d.value}>{d.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <select
                                                className="ag-input text-xs py-1.5 w-full min-w-[100px] max-w-[130px]"
                                                value={fieldValue(u, 'officeBranch')}
                                                onChange={e => setField(u._id, 'officeBranch', e.target.value)}
                                                disabled={isViewOnly}
                                            >
                                                <option value="">—</option>
                                                {officeOptions.map(b => (
                                                    <option key={b} value={b}>{b}</option>
                                                ))}
                                                {fieldValue(u, 'officeBranch') && !officeOptions.includes(fieldValue(u, 'officeBranch')) && (
                                                    <option value={fieldValue(u, 'officeBranch')}>{fieldValue(u, 'officeBranch')}</option>
                                                )}
                                            </select>
                                        </td>
                                        <td className="text-xs text-ink-500 whitespace-nowrap">
                                            {new Date(u.createdAt).toLocaleDateString('en-IN')}
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-end gap-0.5">
                                                <Link
                                                    to={`/admin/users/${u._id}`}
                                                    title="Profile"
                                                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-600 hover:bg-ink-100 transition-colors"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Link>
                                                {!isViewOnly && (
                                                    <>
                                                        <IconBtn title="Change role" onClick={() => changeRole(u)}>
                                                            <UserCog className="h-4 w-4" />
                                                        </IconBtn>
                                                        <IconBtn title="Reset password" onClick={() => reset(u)}>
                                                            <KeyRound className="h-4 w-4" />
                                                        </IconBtn>
                                                        {u.status === 'active' && (
                                                            <IconBtn title="Suspend" danger onClick={() => suspend(u)}>
                                                                <ShieldOff className="h-4 w-4" />
                                                            </IconBtn>
                                                        )}
                                                        {u.status === 'suspended' && (
                                                            <IconBtn title="Reactivate" primary onClick={() => reactivate(u)}>
                                                                <ShieldCheck className="h-4 w-4" />
                                                            </IconBtn>
                                                        )}
                                                        {String(u._id) !== String(me?._id) && (
                                                            <IconBtn title="Delete user" danger onClick={() => removeUser(u)}>
                                                                <Trash2 className="h-4 w-4" />
                                                            </IconBtn>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center text-ink-500 py-10">
                                        No users match these filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/* K2 — small modal for the view-only invite flow. Asks for name + email
   only; backend generates a temp password, creates the user as view_only
   with status=active, optionally emails the credentials. The temp password
   is also returned in the response so admin can copy it manually if SMTP
   isn't configured. */
function InviteViewOnlyModal({ onClose }) {
    const [form, setForm] = useState({ name: '', email: '' });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post('/users/invite-view-only', {
                name: form.name.trim(),
                email: form.email.trim(),
            });
            setResult(data);
            toast.success(`View-only access created for ${data.user?.name}`);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Invite failed');
        } finally { setBusy(false); }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied');
        } catch { /* clipboard might be blocked */ }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => onClose(!!result)}>
            <div className="ag-card max-w-md w-full p-0 overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-ink-100 flex items-start justify-between">
                    <div>
                        <div className="font-extrabold text-base flex items-center gap-2">
                            <EyeIcon className="h-4 w-4 text-brand-teal-700" /> Invite View-Only User
                        </div>
                        <div className="text-xs text-ink-500 mt-1">
                            Read-only auditor — sees every admin & sales page, can't create / edit / delete anything.
                        </div>
                    </div>
                    <button className="ag-btn-ghost" onClick={() => onClose(!!result)}>
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {!result ? (
                    <form onSubmit={submit} className="p-5 space-y-3">
                        <div>
                            <label className="ag-label">Full name *</label>
                            <input className="ag-input" value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Anjali Verma" required />
                        </div>
                        <div>
                            <label className="ag-label">Work email *</label>
                            <input type="email" className="ag-input" value={form.email}
                                onChange={e => setForm({ ...form, email: e.target.value })}
                                placeholder="auditor@example.com" required />
                        </div>
                        <div className="text-[11px] text-ink-500">
                            We'll auto-generate a temporary password and email it to them.
                            You'll also see it on the next screen in case you need to share it manually.
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                            <button type="submit" className="ag-btn-primary" disabled={busy || !form.name.trim() || !form.email.trim()}>
                                <UserPlus className="h-4 w-4" /> {busy ? 'Inviting…' : 'Create view-only access'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="p-5 space-y-3">
                        <div className="text-sm font-semibold text-emerald-700 flex items-center gap-1">
                            <ShieldCheck className="h-4 w-4" /> View-only access created
                        </div>
                        <div className="rounded-lg border border-ink-100 bg-ink-50/50 p-3 space-y-2 text-sm">
                            <KeyValueRow label="Name"  value={result.user?.name} />
                            <KeyValueRow label="Email" value={result.user?.email} onCopy={copy} />
                            <KeyValueRow label="Temporary password" value={result.tempPassword} mono onCopy={copy} />
                        </div>
                        <div className={`text-xs px-3 py-2 rounded border ${result.emailSent ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-amber-50 border-amber-100 text-amber-800'}`}>
                            <Mail className="h-3.5 w-3.5 inline mr-1" />
                            {result.emailSent
                                ? 'Login details sent to their email.'
                                : 'SMTP not configured — copy the password above and share it securely.'}
                        </div>
                        <div className="flex justify-end pt-1">
                            <button className="ag-btn-primary" onClick={() => onClose(true)}>Done</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function KeyValueRow({ label, value, mono = false, onCopy }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] uppercase tracking-wide text-ink-500 font-bold w-32 shrink-0">{label}</span>
            <span className={`flex-1 truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
            {onCopy && (
                <button className="ag-btn-ghost text-xs px-2 py-1" onClick={() => onCopy(value)}>
                    <Copy className="h-3 w-3" />
                </button>
            )}
        </div>
    );
}
